/**
 * SecureAuthService - Production-grade authentication service
 *
 * Handles the authentication boundary:
 * - Login/logout with secure token management
 * - Session validation
 * - Auth state checks
 *
 * Token refresh lifecycle (lock, retry, 401 queueing) is delegated
 * to TokenRefreshService via composition.
 */

import { tokenService } from './TokenService';
import { TokenRefreshService } from './TokenRefreshService';
import type { RetryableRequest } from './TokenRefreshService';
import { cookieUtils } from '../utils/cookieUtils';
import { API_URL } from '@/core/config/urls';
import { authLogger } from '@/core/utils/logger';
import { getUserFriendlyError } from '@voley/shared-utils';
import { getErrorMessage } from '@voley/shared-utils';
import { trackLogin } from '../utils/authTelemetry';
import { getCachedCsrfToken } from '@/core/providers/CsrfProvider';
import { setSessionStatus } from '../sessionStatus';

interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

interface AuthResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  refresh_token?: string; // Only included in login response
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    organizationId: string;
  };
}

interface AuthResult {
  success: boolean;
  user?: AuthResponse['user'];
  error?: string;
}

interface RequestConfig extends RequestInit {
  headers?: Record<string, string>;
}

class SecureAuthService {
  private static instance: SecureAuthService;

  private readonly API_BASE = API_URL;
  private readonly AUTH_ENDPOINTS = {
    login: '/auth/login',
    logout: '/auth/logout',
    validate: '/auth/validate',
  };

  // Flag to prevent in-flight refreshes from re-establishing auth during logout
  private logoutInProgress: boolean = false;

  private tokenRefresh: TokenRefreshService;

  private constructor() {
    this.tokenRefresh = new TokenRefreshService({
      onAuthLost: () => this.handleAuthenticationLost(),
      isLogoutInProgress: () => this.logoutInProgress,
    });

    // Token expired listener — covers the gap left by removing SessionExpiryToast.
    // If the token expires without a successful refresh, surface the overlay.
    tokenService.on('token:expired', () => {
      authLogger.warn('[SecureAuthService] Token expired event received, triggering auth lost');
      this.handleAuthenticationLost();
    });
  }

  static getInstance(): SecureAuthService {
    if (!SecureAuthService.instance) {
      SecureAuthService.instance = new SecureAuthService();
    }
    return SecureAuthService.instance;
  }

  /**
   * Clear all stale auth state before login
   */
  private async clearStaleAuthState(): Promise<void> {
    authLogger.info('[SecureAuthService] Clearing stale auth state before login');

    // Clear tokens
    tokenService.clearTokens();

    // Clear all auth cookies including CSRF
    cookieUtils.clearAllAuthCookies();

    // Clear sessionStorage auth-related items
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.removeItem('auth_state_cache');
        sessionStorage.removeItem('e2e_mock_user');
      } catch (e) {
        authLogger.warn('[SecureAuthService] Failed to clear sessionStorage:', e);
      }
    }

    // Clear CSRF cache (both variable and cookie)
    try {
      const { clearCsrfCache } = await import('@/api/core/baseQueryWithRetry');
      clearCsrfCache();
    } catch (e) {
      authLogger.warn('[SecureAuthService] Failed to clear CSRF cache:', e);
    }
  }

  /**
   * Secure login flow
   */
  async login(credentials: LoginCredentials): Promise<AuthResult> {
    try {
      authLogger.info('[SecureAuthService] Starting login flow');
      this.logoutInProgress = false;

      // Clear all stale auth state before login attempt
      await this.clearStaleAuthState();

      // Make login request
      const response = await fetch(`${this.API_BASE}${this.AUTH_ENDPOINTS.login}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
          rememberMe: credentials.rememberMe,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        const friendlyError = getUserFriendlyError({ status: response.status, data: error });
        throw new Error(friendlyError.message);
      }

      const result = await response.json();

      if (result.status !== 'success' || !result.data?.session || !result.data?.user) {
        throw new Error(result.message || 'Login failed');
      }

      // Don't store session tokens in memory - rely on httpOnly cookies for auth
      // Use server-provided expiresIn (matches JWT TTL, typically 14400s/4h)
      const serverExpiresIn = result.data.session?.expiresIn;
      tokenService.setTokens({
        accessToken: '', // Empty string - use cookies only
        expiresIn: serverExpiresIn || 14400, // Use server value; fallback to 4h
        tokenType: 'Bearer',
        isCookieAuth: true,
      });

      authLogger.info('[SecureAuthService] Login successful');

      // Track successful login
      trackLogin(true, { email: credentials.email });

      return {
        success: true,
        user: {
          id: result.data.user.id,
          email: result.data.user.email,
          firstName: result.data.user.firstName,
          lastName: result.data.user.lastName,
          organizationId: result.data.user.organizationId,
        },
      };
    } catch (error) {
      authLogger.error('[SecureAuthService] Login error:', error);

      // Track failed login
      trackLogin(false, {
        email: credentials.email,
        error: getErrorMessage(error)
      });

      return {
        success: false,
        error: getErrorMessage(error),
      };
    }
  }

  /**
   * Perform comprehensive local auth cleanup
   */
  private async performLocalAuthCleanup(): Promise<void> {
    // Clear tokens
    tokenService.clearTokens();

    // Clear ALL auth-related cookies including CSRF
    cookieUtils.clearAllAuthCookies();

    // Clear sessionStorage auth-related items
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.removeItem('auth_state_cache');
        sessionStorage.removeItem('e2e_mock_user');
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (key.includes('auth') || key.includes('session') || key.includes('csrf'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
      } catch (e) {
        authLogger.warn('[SecureAuthService] Failed to clear sessionStorage:', e);
      }
    }

    // Clear CSRF cache (both variable and cookie)
    try {
      const { clearCsrfCache } = await import('@/api/core/baseQueryWithRetry');
      clearCsrfCache();
    } catch (e) {
      authLogger.warn('[SecureAuthService] Failed to clear CSRF cache:', e);
    }
  }

  /**
   * Secure logout flow
   */
  async logout(): Promise<void> {
    try {
      authLogger.info('[SecureAuthService] Starting logout flow');
      this.logoutInProgress = true;

      // Get current token for logout request (before clearing)
      const token = tokenService.getAccessToken();
      const csrfToken = getCachedCsrfToken();

      // Unsubscribe from push notifications before clearing auth state
      await this.unsubscribePushOnLogout(token, csrfToken);

      // Clear local state immediately (fail-safe - ensures logout even if server fails)
      await this.performLocalAuthCleanup();

      // Notify server to invalidate session with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        await fetch(`${this.API_BASE}${this.AUTH_ENDPOINTS.logout}`, {
          method: 'POST',
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          credentials: 'include',
          signal: controller.signal,
        });
      } catch (error) {
        authLogger.warn('[SecureAuthService] Logout request failed (local cleanup already done):', error);
      } finally {
        clearTimeout(timeoutId);
      }

      authLogger.info('[SecureAuthService] Logout successful');
    } catch (error) {
      authLogger.error('[SecureAuthService] Logout error:', error);
      await this.performLocalAuthCleanup();
    }
  }

  /**
   * Unsubscribe from browser push notifications on logout.
   */
  private async unsubscribePushOnLogout(
    token: string | null,
    csrfToken: string | null,
  ): Promise<void> {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      const endpoint = subscription.endpoint;

      // Unsubscribe browser-side first
      await subscription.unsubscribe();

      // Notify backend to remove the subscription record (best-effort)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      try {
        await fetch(`${this.API_BASE}/notifications/push/unsubscribe`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({ endpoint }),
          signal: controller.signal,
        });
      } catch {
        // Best-effort — daily cleanup job will catch orphaned subscriptions
      } finally {
        clearTimeout(timeoutId);
      }

      authLogger.info('[SecureAuthService] Push subscription removed on logout');
    } catch (error) {
      authLogger.warn('[SecureAuthService] Failed to unsubscribe push on logout:', error);
    }
  }

  // Token refresh — delegated to TokenRefreshService

  async refreshAccessToken(): Promise<void> {
    return this.tokenRefresh.refreshAccessToken();
  }

  async handle401Response(retryRequest: RetryableRequest): Promise<Response> {
    return this.tokenRefresh.handle401Response(retryRequest);
  }

  // Session validation & auth checks

  /**
   * Validate current session using httpOnly cookies
   */
  async validateSession(): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    if (process.env.NODE_ENV === 'development') {
      authLogger.debug('[AUTH_DEBUG] validateSession starting...', {
        endpoint: `${this.API_BASE}${this.AUTH_ENDPOINTS.validate}`,
        cookies: typeof document !== 'undefined' ? document.cookie : 'N/A',
      });
    }

    try {
      const response = await fetch(`${this.API_BASE}${this.AUTH_ENDPOINTS.validate}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (process.env.NODE_ENV === 'development') {
        authLogger.debug('[AUTH_DEBUG] validateSession response', {
          status: response.status,
          ok: response.ok,
        });
      }

      // 5xx server errors: fail open — infrastructure issues, not auth failures
      if (response.status >= 500) {
        authLogger.warn(`[SecureAuthService] Server error ${response.status} during validation, failing open`);
        return true;
      }

      // Check for invalid session token (400)
      if (response.status === 400) {
        authLogger.warn('[SecureAuthService] Invalid session token during validation');
        return false;
      }

      // 4xx auth errors (401, 403, etc.)
      if (!response.ok) {
        authLogger.warn(`[SecureAuthService] Auth validation failed with status ${response.status}`);
        return false;
      }

      const result = await response.json();
      return (result.success === true || result.status === 'success') && result.data?.valid;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === 'AbortError') {
        authLogger.warn('[SecureAuthService] Session validation timed out, failing open');
        return true;
      }
      authLogger.warn('[SecureAuthService] Session validation error:', err);
      return true; // Fail open on network errors
    }
  }

  /**
   * Attach authentication headers to request config
   */
  attachAuthHeaders(config: RequestConfig): RequestConfig {
    const authHeader = tokenService.getAuthorizationHeader();
    const csrfToken = getCachedCsrfToken();

    return {
      ...config,
      headers: {
        ...config.headers,
        ...(authHeader && { 'Authorization': authHeader }),
        ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
      },
    };
  }

  /**
   * Handle complete authentication loss.
   * Guarded against multiple concurrent calls — only the first one redirects.
   */
  private authLostInProgress = false;

  public handleAuthenticationLost(): void {
    if (this.authLostInProgress) {
      authLogger.info('[SecureAuthService] Authentication lost already being handled, skipping duplicate');
      return;
    }
    this.authLostInProgress = true;

    authLogger.info('[SecureAuthService] Authentication lost, showing session overlay');

    // Surface the overlay BEFORE cleanup/redirect so the user sees it immediately
    setSessionStatus('expired');

    // Reject any remaining queued requests before cleanup
    this.tokenRefresh.abortPendingRequests();

    // Clear all auth state synchronously (don't await - we're redirecting)
    this.performLocalAuthCleanup().catch(e => {
      authLogger.warn('[SecureAuthService] Cleanup error during auth loss:', e);
    });

    // Redirect to login (with return URL)
    if (typeof window !== 'undefined') {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?returnUrl=${returnUrl}`;
    }
  }

  /**
   * Check if user is authenticated (quick synchronous check).
   * For authoritative validation, use validateSession().
   */
  isAuthenticated(): boolean {
    if (typeof window === 'undefined') return false;

    const cookies = document.cookie.split('; ').filter(Boolean);

    const hasCsrfToken = cookies.some(c => c.startsWith('csrf-token='));
    const hasJwtAccess = cookies.some(c => c.startsWith('jwt_access='));
    const hasJwtRefresh = cookies.some(c => c.startsWith('jwt_refresh='));
    const hasJustLoggedInHint = cookies.some(c => c.startsWith('auth_just_logged_in=1'));
    const hasLegacyStytch = cookies.some(c => c.startsWith('stytch_session='));

    const result = hasCsrfToken || hasJwtAccess || hasJwtRefresh || hasJustLoggedInHint || hasLegacyStytch;

    if (process.env.NODE_ENV === 'development') {
      authLogger.debug('[AUTH_DEBUG] isAuthenticated check', {
        result,
        hasCsrfToken,
        hasJwtAccess,
        hasJwtRefresh,
        hasJustLoggedInHint,
        hasLegacyStytch,
        cookieCount: cookies.length,
      });
    }

    return result;
  }

  /**
   * Get authentication status
   */
  getAuthStatus() {
    return {
      isAuthenticated: this.isAuthenticated(),
      tokenStatus: tokenService.getStatus(),
      queuedRequests: this.tokenRefresh.getQueuedRequestCount(),
    };
  }
}

// Export singleton instance
export const secureAuthService = SecureAuthService.getInstance();

// Export types
export type { LoginCredentials, AuthResult, RequestConfig };
// Re-export RetryableRequest for any external consumers
export type { RetryableRequest } from './TokenRefreshService';
